/**
 * The theme primitives `@pokpok/astryx` used to import from
 * `@astryxdesign/core/theme`.
 *
 * # THIS IS A REIMPLEMENTATION, NOT A RECOVERY
 *
 * The Astryx monorepo was never in any repository and is on no machine anybody
 * still has — searched again on 2026-08-08, including a second copy found on
 * the Desktop which turned out to be byte-identical to the review snapshot
 * already committed here. So `@astryxdesign/core` is gone, and this file is
 * written **from the shape the consuming code requires**, not from the original
 * source.
 *
 * That distinction matters and must not be smoothed over: the *contract* is
 * recovered exactly — every import in `pokpokTheme.ts`, `MoneyAmount.tsx`,
 * `SyncStatusChip.tsx` and `VerifiedMoneyBadge.tsx` is satisfied with the same
 * names and the same shapes — but the *behaviour* beyond what those call sites
 * exercise is new work and may differ from whatever Astryx did. If the original
 * ever resurfaces, this is the file to diff, and it should not be assumed to
 * agree.
 *
 * What the call sites actually required, and therefore all this promises:
 *
 *   * `defineTheme(config)` — takes name, typography, motion, tokens and
 *     component overrides, and returns something that can emit CSS custom
 *     properties for a light and a dark surface.
 *   * `TokenName` / `TokenValue` — a CSS custom property name, and either one
 *     value or a `[light, dark]` pair.
 *   * `typeScaleVars` — the type-scale custom properties, referenced by name.
 *
 * # `components` used to be typed and never read
 *
 * Until 2026-08-10 `components` was accepted by the config, stored on the
 * returned theme, and read by neither `variables()` nor `css()`. So
 * `pokpokTheme`'s outlined-destructive button emitted nothing, and the red rule
 * `00-foundations.md:176-177` calls binding — *"Destructive actions are outlined
 * in Dried Chili, never filled. There is no `actionDestructiveFill` token
 * anywhere in the system, on purpose"* — was written down and unimplemented on
 * the web side. It is now flattened into custom properties (see
 * `componentVariables`), because that is the one emission shape that costs the
 * existing consumer nothing: `pokpok-web/src/theme/ThemeStyles.tsx` wraps
 * `css(scheme)` in `:root { … }`, so this function may only ever return
 * declarations, never rules.
 */

/** A CSS custom property name. Loose on purpose: the token set is the theme's. */
export type TokenName = `--${string}`;

/**
 * One value, or a `[light, dark]` pair.
 *
 * The pair is the whole reason this type exists rather than `string`.
 * `pokpokTheme` writes `['#D92323', '#F0442E']` for the accent because Chili Red
 * is the light-surface value and Chili Glow is the dark one, and a theme that
 * collapsed the pair would put a colour meant for a dark kitchen screen onto a
 * white till.
 */
export type TokenValue = string | readonly [light: string, dark: string];

export interface ThemeTypography {
  scale: {base: number; ratio: number};
  body: {family: string; fallbacks: string};
  heading: {family: string; fallbacks: string; weights?: Record<number, string>};
  code: {family: string; fallbacks: string};
}

export interface ThemeMotion {
  fast: number;
  medium: number;
  slow: number;
  ratio?: number;
}

/**
 * Component to slot to declarations — e.g.
 * `{button: {base: {…}, 'variant:destructive': {…}}}`.
 *
 * A slot key is either `base` or `<kind>:<name>` (`variant:destructive`,
 * `target:touch`). Only the `<name>` half reaches the custom property, so the
 * kind is free documentation for the reader and costs nothing at the surface.
 */
export type ComponentOverrides = Record<string, Record<string, Record<string, string>>>;

export interface ThemeConfig<Density extends string = string> {
  name: string;
  typography: ThemeTypography;
  motion: ThemeMotion;
  tokens: Partial<Record<TokenName, TokenValue>>;
  components?: ComponentOverrides;
  /**
   * Named layers of component overrides, applied over `components`.
   *
   * This exists because one product runs on two input models. Foundations' 48px
   * button floor is the wet-thumb POS minimum; the back office is a
   * mouse-driven desktop surface whose floor is the ordinary web accessibility
   * minimum (`05-back-office.md:34-39`). A density is a layer here rather than
   * a per-screen override because the back office is ~30 screens, and 30
   * screens each restating a floor is 30 chances to restate it wrong.
   */
  densities?: Record<Density, ComponentOverrides>;
}

export interface Theme<Density extends string = string> extends ThemeConfig<Density> {
  /**
   * The token values for one surface, custom property name to value.
   *
   * Omitting `density` gives the base layer, which for `pokpokTheme` is the POS
   * one. That default is deliberate: the till is the surface where getting it
   * wrong hurts someone mid-service.
   */
  variables(scheme: 'light' | 'dark', density?: Density): Record<string, string>;
  /** Those variables as a CSS rule body, for a stylesheet or a `<style>`. */
  css(scheme: 'light' | 'dark', density?: Density): string;
}

/**
 * The type scale, as custom property **references**.
 *
 * They are `var(...)` strings rather than sizes because a component must not
 * bake a number: the scale is set once per theme from
 * `typography.scale.{base,ratio}`, and a component that hard-coded 16px would
 * stop responding to it.
 */
export const typeScaleVars = {
  '--text-supporting-size': 'var(--text-supporting-size)',
  '--text-supporting-leading': 'var(--text-supporting-leading)',
  '--text-body-size': 'var(--text-body-size)',
  '--text-body-leading': 'var(--text-body-leading)',
  '--text-heading-1-size': 'var(--text-heading-1-size)',
  '--text-heading-1-leading': 'var(--text-heading-1-leading)',
} as const;

/** Steps of the modular scale, smallest first, keyed to the names above. */
const SCALE_STEPS = [
  ['--text-supporting', -1],
  ['--text-body', 0],
  ['--text-heading-1', 3],
] as const;

function round(value: number): string {
  return `${Math.round(value * 1000) / 1000}px`;
}

/** `minHeight` → `min-height`, `confirmMoney` → `confirm-money`. */
function kebab(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/[^A-Za-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
}

/**
 * Flatten component overrides into custom properties, later layers winning.
 *
 * Custom properties rather than CSS rules, because a rule needs a selector and
 * this package has no Button to attach one to — and inventing one would be
 * inventing the desktop primitive library, which is a separate decision. A
 * token any component can read is the part that is knowably right today, and it
 * is the part that stops 30 screens from each re-deriving the same value.
 *
 * `{button: {'variant:destructive': {borderColor: '…'}}}` on a theme named
 * `pokpok` becomes `--pokpok-button-destructive-border-color`. `base` adds no
 * segment, so `button.base.minHeight` is `--pokpok-button-min-height`.
 */
function componentVariables(
  themeName: string,
  layers: readonly (ComponentOverrides | undefined)[],
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const layer of layers) {
    if (layer === undefined) continue;
    for (const [component, slots] of Object.entries(layer)) {
      for (const [slot, declarations] of Object.entries(slots)) {
        const separator = slot.indexOf(':');
        const slotSegment =
          slot === 'base' ? '' : kebab(separator === -1 ? slot : slot.slice(separator + 1));
        for (const [property, value] of Object.entries(declarations)) {
          const name = [`--${kebab(themeName)}`, kebab(component), slotSegment, kebab(property)]
            .filter((segment) => segment !== '')
            .join('-');
          out[name] = value;
        }
      }
    }
  }
  return out;
}

export function defineTheme<const Density extends string = never>(
  config: ThemeConfig<Density>,
): Theme<Density> {
  const densityLayer = (density: Density | undefined): ComponentOverrides | undefined => {
    if (density === undefined) return undefined;
    const layer = config.densities?.[density];
    if (layer === undefined) {
      const declared = Object.keys(config.densities ?? {});
      // Throwing beats falling back to the base layer. A mistyped density that
      // silently returned the default would put the POS 48px tap floor on a
      // desktop back-office screen, render correctly enough to pass review, and
      // say nothing — which is the failure mode this whole layer exists to end.
      throw new Error(
        `theme "${config.name}" has no density "${density}" ` +
          `(declared: ${declared.length === 0 ? 'none' : declared.join(', ')})`,
      );
    }
    return layer;
  };

  const variables = (
    scheme: 'light' | 'dark',
    density?: Density,
  ): Record<string, string> => {
    // Component variables go in first so an explicit `tokens` entry wins on a
    // name collision: `tokens` is a direct statement, a component variable is
    // derived from one.
    const out: Record<string, string> = componentVariables(config.name, [
      config.components,
      densityLayer(density),
    ]);

    for (const [name, value] of Object.entries(config.tokens)) {
      if (value === undefined) continue;
      // A bare string is scheme-independent — `--color-on-accent` is white on
      // both, and pairing it would be inventing a second value nobody chose.
      out[name] = typeof value === 'string' ? value : value[scheme === 'light' ? 0 : 1];
    }

    const {base, ratio} = config.typography.scale;
    for (const [name, step] of SCALE_STEPS) {
      const size = base * Math.pow(ratio, step);
      out[`${name}-size`] = round(size);
      // Leading tightens as type grows: a heading set at body line-height reads
      // as a paragraph, and body set at heading line-height reads as a list.
      out[`${name}-leading`] = round(size * (step >= 3 ? 1.25 : 1.5));
    }

    out['--font-body'] = `${config.typography.body.family}, ${config.typography.body.fallbacks}`;
    out['--font-heading'] =
      `${config.typography.heading.family}, ${config.typography.heading.fallbacks}`;
    out['--font-code'] = `${config.typography.code.family}, ${config.typography.code.fallbacks}`;

    out['--motion-fast'] = `${config.motion.fast}ms`;
    out['--motion-medium'] = `${config.motion.medium}ms`;
    out['--motion-slow'] = `${config.motion.slow}ms`;

    return out;
  };

  return {
    ...config,
    variables,
    css: (scheme, density) =>
      Object.entries(variables(scheme, density))
        .map(([name, value]) => `  ${name}: ${value};`)
        .join('\n'),
  };
}
